import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Radio, ArrowLeft, Ban, RefreshCw, Loader2, Globe, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const relTime = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 5) return 'now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return `${m}m ago`;
};

const platformFromReferrer = (ref) => {
  if (!ref) return 'Direct';
  try {
    const host = new URL(ref).hostname.replace(/^www\./, '');
    if (host.includes('google')) return 'Google';
    if (host.includes('facebook') || host.includes('fb.')) return 'Facebook';
    if (host.includes('instagram')) return 'Instagram';
    if (host.includes('tiktok')) return 'TikTok';
    if (host.includes('twitter') || host.includes('x.com')) return 'X / Twitter';
    if (host.includes('youtube')) return 'YouTube';
    if (host.includes('bing')) return 'Bing';
    if (host.includes('spotify')) return 'Spotify';
    return host;
  } catch {
    return 'Direct';
  }
};

const LiveVisitorsPage = () => {
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchNow = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/live-visitors`);
      setVisitors(data.visitors || []);
      setCount(data.count || 0);
    } catch {
      // silent — the auto-refresh will try again
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNow();
    intervalRef.current = setInterval(fetchNow, 5000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const blockIp = async (ip) => {
    if (!ip) return;
    if (!window.confirm(`Block IP ${ip}? Future checkouts from this address will be refused.`)) return;
    try {
      await axios.post(`${API}/admin/blocklist`, { type: 'ip', value: ip, reason: 'Blocked from Live Visitors' });
      toast.success(`IP ${ip} blocked`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Block failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-6xl mx-auto p-6 sm:p-10">
        <button onClick={() => navigate('/admin/home')} className="text-xs text-[#71717A] hover:text-[#00F0FF] inline-flex items-center gap-1 mb-2">
          <ArrowLeft size={12} /> Admin home
        </button>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Radio className="text-[#00F0FF] animate-pulse" size={28} />
            <div>
              <h1 className="text-3xl font-heading font-bold">Live visitors</h1>
              <p className="text-sm text-[#A1A1AA]">{count} active now · auto-refresh every 5s · 60s window</p>
            </div>
          </div>
          <button
            onClick={fetchNow}
            className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[#18181B] border border-[#27272A] hover:border-[#00F0FF] hover:text-[#00F0FF] transition"
            data-testid="live-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#71717A]"><Loader2 className="mx-auto animate-spin mb-3" /> Loading live visitors…</div>
        ) : visitors.length === 0 ? (
          <div className="text-center py-16 bg-[#121214] border border-[#27272A] rounded-lg" data-testid="live-empty">
            <Globe className="mx-auto text-[#27272A] mb-3" size={40} />
            <p className="text-sm text-[#71717A]">No active visitors in the last 60 seconds.</p>
          </div>
        ) : (
          <div className="bg-[#121214] border border-[#27272A] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0F0F11] text-[10px] uppercase tracking-wider text-[#71717A]">
                <tr>
                  <th className="text-left px-4 py-2">IP</th>
                  <th className="text-left px-4 py-2">Buyer</th>
                  <th className="text-left px-4 py-2">Page</th>
                  <th className="text-left px-4 py-2">Platform</th>
                  <th className="text-left px-4 py-2"><Clock size={10} className="inline" /> Last seen</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((v) => (
                  <tr key={v.session_id} className="border-t border-[#27272A]" data-testid={`live-row-${v.session_id}`}>
                    <td className="px-4 py-3 font-mono text-xs break-all">{v.ip_address || '—'}</td>
                    <td className="px-4 py-3">
                      {v.buyer_email ? (
                        <div>
                          <div className="text-xs text-white">{v.buyer_name || '—'}</div>
                          <div className="text-[10px] text-[#00F0FF]">{v.buyer_email}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-[#71717A]">New visitor</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#E4E4E7] font-mono max-w-[220px] truncate" title={v.path}>{v.path}</td>
                    <td className="px-4 py-3 text-xs">
                      <span className="px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 text-[10px]">
                        {platformFromReferrer(v.referrer)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#A1A1AA]">{relTime(v.last_seen_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => blockIp(v.ip_address)}
                        disabled={!v.ip_address}
                        className="text-xs px-2 py-1 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-30 inline-flex items-center gap-1"
                        data-testid={`live-block-${v.session_id}`}
                      >
                        <Ban size={10} /> Block
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveVisitorsPage;
